import {
  ArrowRight,
  BookmarkCheck,
  Crosshair,
  History,
  Network,
  OctagonAlert,
  RefreshCw,
  Wallet,
  type LucideIcon,
} from "lucide-react";
import { Fragment, useRef, type ReactNode, useEffect } from "react";
import { Link } from "react-router-dom";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaCheckbox } from "@/shared/components/forms/ColocaCheckbox";
import { ColocaDialog } from "@/shared/components/overlays/ColocaDialog";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { useAmountFormatter } from "../../hooks/useAmountFormatter";
import type { CryptoSwapSubmitPhase } from "../../hooks/useCryptoSwapForm";
import { useCryptoSwapLabels } from "../../hooks/useCryptoSwapLabels";
import { useOtcTiming } from "../../hooks/useOtcTiming";
import type { OtcSellPair, OtcSellQuote } from "../../types/cryptoSell.types";
import { SELL_ORDER_OPEN_CODE } from "../../utils/cryptoSellErrors";
import type { CryptoSwapErrorKind } from "../../utils/cryptoSwapErrors";
import { amountPrecision, roundsToZero, slotMarker, splitTemplate } from "../../utils/cryptoSwapFormat";
import { CRYPTO_SELL_HISTORY_PATH } from "../../utils/cryptoSwapLinks";
import type { CryptoSwapBlock } from "../../utils/cryptoSwapStatus";
import { AssetAvatar, MonoValue } from "../shared/CryptoSwapBits";
import { EstimateLine } from "../shared/OperationEta";
import { SellTransit } from "../shared/OtcTransit";
import { AddressReview } from "../swap/ConfirmSwapDialog";
import { QuoteTimerBar } from "../swap/QuoteTimerBar";

export interface ConfirmSellDialogProps {
  open: boolean;
  onClose: () => void;
  pair: OtcSellPair | null;
  quote: OtcSellQuote | null;
  isPricing: boolean;
  network: string;
  /** The wallet the deposit comes from and returns to. */
  refundAddress: string;
  walletNote?: string | null;
  concept: string;
  clockOffsetMs: number;
  block: CryptoSwapBlock;
  blockMessage: string | null;
  quoteErrorMessage: string | null;
  acknowledged: boolean;
  onAcknowledgedChange: (acknowledged: boolean) => void;
  phase: CryptoSwapSubmitPhase;
  errorKind: CryptoSwapErrorKind | null;
  errorCode: string | null;
  errorMessage: string | null;
  onConfirm: () => void;
  onRequote: () => void;
}

/** Last stop before an address is issued: the three rules a deposit must follow, acknowledged as read. */
export function ConfirmSellDialog({
  open,
  onClose,
  pair,
  quote,
  isPricing,
  network,
  refundAddress,
  walletNote,
  concept,
  clockOffsetMs,
  block,
  blockMessage,
  quoteErrorMessage,
  acknowledged,
  onAcknowledgedChange,
  phase,
  errorKind,
  errorCode,
  errorMessage,
  onConfirm,
  onRequote,
}: ConfirmSellDialogProps) {
  const { t } = useTranslations("cryptoSwaps");
  const timing = useOtcTiming();
  const headlineRef = useRef<HTMLParagraphElement>(null);

  // Whatever held focus may have unmounted with the price; keep it inside the dialog.
  useEffect(() => {
    if (open && !quote && document.activeElement === document.body) headlineRef.current?.focus();
  }, [open, quote]);
  const labels = useCryptoSwapLabels();
  const format = useAmountFormatter(pair);
  const rule = labels.networkRule(network);
  const networkLabel = labels.network(network);
  const asset = quote?.send.currency ?? pair?.send_currency ?? "";

  // Unknown outcome, or a sale already waiting: the history is the honest exit, not another attempt.
  const toHistory = errorKind === "unconfirmed" || errorCode === SELL_ORDER_OPEN_CODE;
  const pricing = isPricing || block === "pricing" || block === "stale";
  const needsPrice = !pricing && (block === "expired" || block === "unpriced" || block === "amount");
  const otherBlock = block !== null && !pricing && !needsPrice;
  const sendText = quote ? `${format(quote.send.amount, quote.send.currency)} ${quote.send.currency}` : "";
  const feeShown = !!quote && !roundsToZero(quote.fee.total, amountPrecision(quote.fee.currency, pair).max);

  const headline = quote
    ? splitTemplate(t("sell_confirm_headline", { send: slotMarker("send"), receive: slotMarker("receive") }))
    : [];
  const slot = (name: string): ReactNode =>
    !quote ? null : name === "send" ? (
      <span className="inline-flex items-baseline gap-1.5 font-mono whitespace-nowrap tabular-nums">
        <AssetAvatar asset={quote.send.currency} kind="ONCHAIN" size="sm" className="self-center" />
        {sendText}
      </span>
    ) : (
      <span className="font-mono whitespace-nowrap tabular-nums">
        {`${format(quote.receive.amount, quote.receive.currency)} ${quote.receive.currency}`}
      </span>
    );

  const primary = needsPrice ? (
    <ColocaButton variant="primary" icon={RefreshCw} onClick={onRequote}>
      {t("confirm_refresh")}
    </ColocaButton>
  ) : (
    <ColocaButton
      variant="primary"
      iconTrailing={ArrowRight}
      loading={pricing}
      disabled={block !== null || !acknowledged || !refundAddress}
      onClick={onConfirm}
    >
      {t("sell_confirm_create")}
    </ColocaButton>
  );

  return (
    <ColocaDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={t("sell_confirm_title")}
      mobileFullscreen
      initialFocus={headlineRef}
      toolbar={
        phase === "creating" || (phase === "failed" && toHistory) ? undefined : (
          <div className="dark rounded-xl border border-border bg-card p-3 text-card-foreground">
            <QuoteTimerBar
              key={quote?.id ?? "none"}
              label={t("confirm_price")}
              quote={quote}
              isPricing={pricing}
              clockOffsetMs={clockOffsetMs}
              announce={open}
            />
          </div>
        )
      }
      footer={
        phase === "creating" ? null : phase === "failed" && toHistory ? (
          <ColocaButton variant="primary" icon={History} render={<Link to={CRYPTO_SELL_HISTORY_PATH} />}>
            {t("sell_error_history")}
          </ColocaButton>
        ) : (
          <>
            <ColocaButton variant="ghost" onClick={onClose}>
              {t("confirm_back")}
            </ColocaButton>
            {primary}
          </>
        )
      }
    >
      {phase === "creating" ? (
        <div role="status" className="flex flex-col items-center gap-3 py-8 text-center">
          <SellTransit
            className="mb-3 max-w-md"
            sendCurrency={asset}
            receiveCurrency={quote?.receive.currency ?? pair?.receive_currency ?? ""}
            network={network}
            payoutKey={pair?.payout_key}
          />
          <p className="text-base font-semibold text-foreground">{t("sell_creating_title")}</p>
          <p className="text-sm text-muted-foreground">{t("order_creating_hint")}</p>
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          {phase === "failed" && errorMessage && (
            <ColocaAlert variant={toHistory ? "warning" : "destructive"} text={errorMessage} />
          )}

          {/* Mounted even between prices: it holds the initial focus, and unmounting it drops focus to the page. */}
          <p
            ref={headlineRef}
            tabIndex={-1}
            className={cn(
              "text-2xl leading-snug font-semibold tracking-tight text-foreground transition-opacity outline-none",
              (pricing || needsPrice) && "opacity-50",
              !quote && "sr-only",
            )}
          >
            {headline.map((part, index) =>
              part.kind === "text" ? (
                <Fragment key={index}>{part.value}</Fragment>
              ) : (
                <Fragment key={index}>{slot(part.name)}</Fragment>
              ),
            )}
          </p>

          {needsPrice && (
            <ColocaAlert
              variant="warning"
              heading={block === "expired" ? t("confirm_expired_title") : undefined}
              text={block === "expired" ? t("confirm_expired_message") : (quoteErrorMessage ?? blockMessage)}
            />
          )}
          {otherBlock && blockMessage && <ColocaAlert variant="warning" text={blockMessage} />}

          {quote && (
            <dl
              className={cn(
                "m-0 grid grid-cols-[auto_1fr] gap-x-4 gap-y-3 text-sm transition-opacity",
                (pricing || needsPrice) && "opacity-50",
              )}
            >
              <Row label={t("confirm_price")} mono>
                {t("price_line", {
                  asset: quote.send.currency,
                  price: format(quote.price.value, quote.price.currency),
                  currency: quote.price.currency,
                })}
              </Row>
              <Row label={t("sell_ticket_proceeds")} mono>
                {`${format(quote.proceeds.amount, quote.proceeds.currency)} ${quote.proceeds.currency}`}
              </Row>
              <Row label={t("sell_ticket_fee")} mono={feeShown}>
                {feeShown
                  ? `${format(quote.fee.total, quote.fee.currency)} ${quote.fee.currency}`
                  : t("sell_ticket_fee_none")}
              </Row>
              <Row label={t("sell_ticket_net")} mono>
                <span className="font-semibold">
                  {`${format(quote.receive.amount, quote.receive.currency)} ${quote.receive.currency}`}
                </span>
              </Row>
              {timing?.sell[network] && (
                <Row label={t("eta_label")}>
                  <EstimateLine estimate={timing.sell[network]} />
                </Row>
              )}
              {concept && <Row label={t("confirm_concept")}>{concept}</Row>}
            </dl>
          )}

          {quote && (
            <section className="flex flex-col gap-3 rounded-xl border border-border bg-muted/40 p-4">
              <h3 className="text-xs font-semibold tracking-[0.08em] text-muted-foreground uppercase">
                {t("sell_confirm_rules_title")}
              </h3>
              <DepositRule
                icon={Network}
                title={t("sell_rule_network_title", { asset, network: networkLabel })}
              >
                {rule && (
                  <>
                    {t("sell_rule_network", { standard: rule.standard, gas: rule.gasAsset })}{" "}
                    <MonoValue breakAll className="text-xs">
                      {rule.usdtContract}
                    </MonoValue>
                  </>
                )}
              </DepositRule>
              <DepositRule icon={Crosshair} title={t("sell_rule_exact_title", { amount: sendText })}>
                {t("sell_rule_exact")}
              </DepositRule>
              <DepositRule icon={Wallet} title={t("sell_rule_wallet_title")}>
                {t("sell_rule_wallet", { amount: sendText })}
              </DepositRule>
            </section>
          )}

          {refundAddress && <AddressReview address={refundAddress} title={t("sell_confirm_refund_title")} />}
          {refundAddress && walletNote && (
            <p className="-mt-2 flex items-center gap-2 text-[13px] text-muted-foreground">
              <BookmarkCheck aria-hidden="true" className="size-4 shrink-0 text-primary" />
              {walletNote}
            </p>
          )}

          <ColocaAlert
            variant="destructive"
            icon={OctagonAlert}
            heading={t("confirm_network_warning_title", { network: networkLabel })}
            text={t("sell_confirm_network_warning", { asset, network: networkLabel })}
          />

          <div
            className={cn(
              "rounded-xl border p-4 transition-colors",
              acknowledged ? "border-primary bg-primary/5" : "border-border bg-card",
            )}
          >
            <ColocaCheckbox
              checked={acknowledged}
              onCheckedChange={onAcknowledgedChange}
              label={
                <span className="leading-snug">
                  {t("sell_confirm_acknowledge", { amount: sendText, network: networkLabel })}
                </span>
              }
            />
          </div>
        </div>
      )}
    </ColocaDialog>
  );
}

function DepositRule({
  icon: Icon,
  title,
  children,
}: {
  icon: LucideIcon;
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="flex gap-3">
      <Icon aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-primary" />
      <div className="flex min-w-0 flex-col gap-0.5">
        <p className="text-sm font-semibold text-foreground">{title}</p>
        <p className="text-[13px] leading-relaxed text-muted-foreground">{children}</p>
      </div>
    </div>
  );
}

function Row({ label, mono, children }: { label: string; mono?: boolean; children: ReactNode }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={cn("m-0 text-right break-words", mono && "font-mono tabular-nums")}>{children}</dd>
    </>
  );
}
